import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Droplets,
  Sprout,
  HeartCrack,
  Sparkles,
  AlertTriangle,
} from "lucide-react";
import NewsletterCTA from "../components/NewsletterCTA";

const PRODUCT_IMG =
  "https://static.prod-images.emergentagent.com/jobs/c27e6d37-3b6f-4f46-8182-c3bde30f7edb/images/b6e9059117124de2d873fd1afc1b9fca3cd41d3f51056bb06984ce0c7f90b902.png";
const MILLET_FIELD_IMG =
  "https://customer-assets.emergentagent.com/job_web-instructions-3/artifacts/g0tkyaa4_pexels-deep-malik-132129520-10738421.webp";
const VIRESH_IMG =
  "https://customer-assets.emergentagent.com/job_web-instructions-3/artifacts/29minnlm_Viresh%20Headshot.jpg";

/* Sticker badge */
const Sticker = ({ children, rotate = 0, color = "butter", className = "", delay = 0 }) => {
  const bg = {
    butter: "bg-butter text-jet",
    pink: "bg-pink text-cream",
    cobalt: "bg-cobalt text-cream",
    lime: "bg-lime text-jet",
    tangerine: "bg-tangerine text-cream",
    cream: "bg-cream text-jet",
    mint: "bg-mint text-jet",
    lilac: "bg-lilac text-jet",
    jet: "bg-jet text-cream",
  }[color];
  return (
    <span
      className={`sticker animate-jiggle ${bg} ${className}`}
      style={{ transform: `rotate(${rotate}deg)`, animationDelay: `${delay}s` }}
    >
      {children}
    </span>
  );
};

const Marquee = ({ items, bg = "bg-butter", fg = "text-jet", reverse = false }) => (
  <div className={`overflow-hidden border-y-2 border-jet ${bg}`}>
    <div
      className={`flex whitespace-nowrap ${reverse ? "animate-marquee-rev" : "animate-marquee"}`}
      style={{ width: "max-content" }}
    >
      {Array.from({ length: 2 }).map((_, loop) => (
        <div key={loop} className="flex items-center gap-10 px-6 py-4">
          {items.map((t, i) => (
            <React.Fragment key={`${loop}-${i}`}>
              <span className={`font-chunk text-4xl md:text-5xl ${fg}`}>{t}</span>
              <span className={`text-3xl ${fg} opacity-70`}>✺</span>
            </React.Fragment>
          ))}
        </div>
      ))}
    </div>
  </div>
);

export default function Home() {
  return (
    <main data-testid="page-home" className="bg-cream">
      {/* ============== HERO — cream with brand-blue + orange ============== */}
      <section
        data-testid="section-hero"
        className="relative min-h-screen overflow-hidden bg-cream pt-32 text-jet md:pt-40"
      >
        {/* floating stickers — removed per brand direction */}

        <div className="relative mx-auto grid max-w-[1500px] items-center gap-10 px-6 pb-20 pt-8 md:px-10 lg:grid-cols-[1.2fr_1fr] lg:gap-14">
          <div className="relative z-10">
            <div className="flex flex-wrap items-center gap-3">
              <Sticker color="cream" rotate={-3}>
                <Sparkles size={14} className="mr-1.5" /> New drop · 2026
              </Sticker>
              <Sticker color="cobalt" rotate={4}>canadian made</Sticker>
            </div>

            <h1 className="mt-8 font-sans text-5xl font-extrabold leading-[1] tracking-tight md:text-7xl lg:text-[6.5rem]">
              The{" "}
              <span className="bg-butter px-2 text-jet">future</span> of
              dairy is{" "}
              <span className="relative inline-block">
                <span className="text-cobalt">millets.</span>
                <span className="absolute -bottom-2 left-0 h-3 w-full rounded-full bg-orange"></span>
              </span>
            </h1>

            <p className="mt-8 max-w-xl font-sans text-lg font-medium text-jet/80 md:text-xl">
              Meet <strong className="text-cobalt">Milet</strong> — plant milk
              made from a 10,000-year-old grain your grandma already trusted.
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="#product" data-testid="hero-cta-product" className="chunk-btn bg-cobalt text-cream">
                See the milk <ArrowUpRight size={16} />
              </a>
              <a
                href="#newsletter"
                data-testid="hero-cta-newsletter"
                className="chunk-btn bg-cream text-jet"
              >
                Get on the list
              </a>
            </div>

            <div className="mt-12 flex items-center gap-5 text-jet/70">
              <div>
                <p className="font-chunk text-4xl text-cobalt">10k+</p>
                <p className="font-sans text-xs font-extrabold uppercase tracking-widest">years old</p>
              </div>
              <div className="h-10 w-px bg-jet/30" />
              <div>
                <p className="font-chunk text-4xl text-orange">1</p>
                <p className="font-sans text-xs font-extrabold uppercase tracking-widest">ingredient</p>
              </div>
              <div className="h-10 w-px bg-jet/30" />
              <div>
                <p className="font-chunk text-4xl text-cobalt">0</p>
                <p className="font-sans text-xs font-extrabold uppercase tracking-widest">regrets</p>
              </div>
            </div>
          </div>

          {/* Product carton */}
          <div className="relative flex items-center justify-center">
            <div className="absolute inset-0 flex items-center justify-center">
              <svg viewBox="0 0 400 400" className="h-[110%] w-[110%] animate-spinslow">
                <defs>
                  <path id="circle" d="M 200,200 m -170,0 a 170,170 0 1,1 340,0 a 170,170 0 1,1 -340,0" />
                </defs>
                <text fill="#F5EDDC" fontSize="18" fontFamily="Bricolage Grotesque" fontWeight="800" letterSpacing="4">
                  <textPath href="#circle">
                    · MILET MILK · NUTS FREE · SOY FREE · LACTOSE FREE · DAIRY FREE · GLUTEN FREE · MILET MILK · NUTS FREE · SOY FREE · LACTOSE FREE
                  </textPath>
                </text>
              </svg>
            </div>
            <div className="relative aspect-square w-[82%] md:w-[78%]">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-cobalt via-cobalt to-cobaltDeep" />
              <div className="absolute inset-2 rounded-full bg-cobalt/80 mix-blend-overlay" />
              <img
                src={PRODUCT_IMG}
                alt="Milet Milk carton"
                className="relative z-10 h-full w-full animate-bob object-contain p-8 drop-shadow-[0_30px_30px_rgba(0,0,0,0.35)]"
              />
              <span className="absolute -left-4 top-6 h-10 w-10 rotate-12 rounded-full bg-butter ring-2 ring-jet" />
              <span className="absolute -right-3 bottom-10 h-6 w-6 rounded-full bg-orange ring-2 ring-jet" />
              <span className="absolute bottom-2 left-8 h-4 w-4 rounded-full bg-cream ring-2 ring-jet" />
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE 1 */}
      <Marquee
        items={["DAIRY-FREE", "NUT-FREE", "SOY-FREE", "LACTOSE-FREE", "GLUTEN-FREE", "NONSENSE-FREE"]}
        bg="bg-butter"
      />

      {/* ============== WHAT IS MILET — cream with millet field ============== */}
      <section
        data-testid="section-what"
        className="relative overflow-hidden bg-cream py-20 md:py-28"
      >
        <div className="mx-auto grid max-w-[1400px] items-start gap-12 px-6 md:grid-cols-2 md:px-10">
          <div className="relative">
            <Sticker color="cobalt" rotate={-6}>§ what even is this</Sticker>
            <h2 className="mt-6 font-sans text-4xl font-extrabold leading-[1.05] tracking-tight text-jet md:text-6xl lg:text-[4.5rem]">
              So,{" "}
              <span className="bg-cobalt px-2 text-cream">what</span> is
              millet?
            </h2>

            <div className="mt-8 space-y-5 font-sans text-lg leading-relaxed text-jet md:text-xl">
              <p>
                Millet is a tiny, ancient grain that's been quietly feeding{" "}
                <strong>40% of the planet for 10,000 years</strong> — before
                wheat got trendy, before almond went viral, before oats made a
                comeback.
              </p>
              <p className="rounded-2xl border-2 border-jet bg-cobalt p-5 font-sans text-2xl font-extrabold leading-snug tracking-tight text-cream md:text-3xl">
                Milet doesn't need a PR team. What the world needs is a{" "}
                <span className="bg-butter px-2 text-jet">reality check!!</span>
              </p>
              <p>
                So we are building one. Naturally gluten-free. Nothing added.
                Nothing stripped. Grown on rain, not on pressure.
              </p>
            </div>

            <Link
              to="/what-is-millet"
              data-testid="what-cta-learn"
              className="chunk-btn mt-8 bg-cobalt text-cream"
            >
              Read the full story <ArrowUpRight size={16} />
            </Link>
          </div>

          {/* RIGHT — millet field image */}
          <div className="relative">
            <div className="relative mx-auto aspect-square w-full max-w-md">
              <div className="absolute inset-0 rotate-3 rounded-[3rem] bg-cobalt" />
              <img
                src={MILLET_FIELD_IMG}
                alt="Pearl millet growing in the field"
                className="relative h-full w-full rounded-[3rem] border-[3px] border-jet object-cover"
              />
              <Sticker color="cream" rotate={10} className="absolute -right-4 -top-4">
                ✱ real millet · real field
              </Sticker>
            </div>
          </div>
        </div>

        <div className="absolute -bottom-6 right-6 hidden md:block">
          <Sticker color="cobalt" rotate={-14} delay={0.8}>real food · loud brand</Sticker>
        </div>
      </section>

      {/* ============== BIG AG CONSPIRACY — cream with cobalt cards ============== */}
      <section
        data-testid="section-big-ag"
        className="relative overflow-hidden border-y-2 border-jet bg-cream py-20 text-jet md:py-28"
      >
        {/* decorative ring */}
        <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full border-[3px] border-dashed border-cobalt/30" />
        <div className="pointer-events-none absolute -bottom-24 -left-20 h-80 w-80 rounded-full border-[3px] border-dashed border-orange/40" />

        <div className="relative mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div className="flex items-center gap-3">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-jet bg-orange text-cream">
                <AlertTriangle size={20} />
              </span>
              <Sticker color="butter" rotate={-3}>
                § then why haven't you heard of it?
              </Sticker>
            </div>
            <Sticker color="cobalt" rotate={4}>
              the receipts are loud
            </Sticker>
          </div>

          <h2 className="mt-10 font-sans text-4xl font-extrabold leading-[1.05] tracking-tight md:text-6xl lg:text-[4.5rem]">
            Big ag spent{" "}
            <span className="bg-butter px-2 text-jet">billions</span> replacing
            millet with{" "}
            <span className="text-cobalt">wheat, corn, soy, oats &amp; almond.</span>
          </h2>

          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {[
              {
                bg: "bg-butter",
                fg: "text-jet",
                icon: "$",
                title: "Billion $ campaigns",
                body:
                  "Decades of marketing dollars shouting 'plant-based' so you'd forget millet was the OG.",
              },
              {
                bg: "bg-cobalt",
                fg: "text-cream",
                icon: "⌘",
                title: "Patented seeds",
                body:
                  "Hybrid wheat, GMO corn, patented oats. Millet grows open-source — no royalties, no moat.",
              },
              {
                bg: "bg-orange",
                fg: "text-cream",
                icon: "⇌",
                title: "Owned supply chain",
                body:
                  "They own the silos, the haulers, the shelves. Millet comes from smallholder farms that refuse to play the game.",
              },
            ].map((c, i) => (
              <div
                key={i}
                className={`relative rounded-[2rem] border-2 border-jet ${c.bg} ${c.fg} p-7 shadow-pop-lg transition-transform hover:-translate-y-2`}
              >
                <span
                  className={`inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-jet bg-cream font-sans text-2xl font-extrabold text-jet`}
                >
                  {c.icon}
                </span>
                <h3 className="mt-5 font-sans text-2xl font-extrabold tracking-tight md:text-3xl">
                  {c.title}
                </h3>
                <p className="mt-3 font-sans text-base leading-relaxed opacity-90 md:text-lg">
                  {c.body}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-12 rounded-[2rem] border-2 border-jet bg-cobalt p-6 text-cream shadow-pop md:p-8">
            <p className="font-sans text-xl font-bold leading-snug md:text-2xl">
              Which is exactly why we want millet back in your daily life —
              without asking permission from anyone.
            </p>
          </div>
        </div>
      </section>

      {/* MARQUEE 2 */}
      <Marquee
        items={["NOT ALMOND", "NOT OAT", "NOT SOY", "NOT A TREND", "JUST MILLET"]}
        bg="bg-orange"
        fg="text-cream"
        reverse
      />

      {/* ============== WHY — cream manifesto with cobalt accent ============== */}
      <section
        data-testid="section-why"
        className="relative overflow-hidden bg-cream py-20 text-jet md:py-28"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <Sticker color="butter" rotate={-4}>§ the manifesto</Sticker>
            <Sticker color="cream" rotate={6}>read it twice</Sticker>
          </div>

          <h2 className="mt-10 font-sans text-5xl font-extrabold leading-[1.05] tracking-tight md:text-6xl lg:text-7xl">
            We're not{" "}
            <span className="bg-butter px-2 text-jet">"plant-based."</span>
            <br />
            We're{" "}
            <span className="relative inline-block">
              plant.
              <span className="absolute -bottom-2 left-0 h-3 w-full rounded-full bg-orange"></span>
            </span>
          </h2>

          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {[
              { bg: "bg-cobalt", fg: "text-cream", icon: <HeartCrack size={32} />, title: "No labels needed", body: "Millet was a supergrain before buzzwords ate the internet. We didn't reinvent food. We just put it back where it belongs." },
              { bg: "bg-orange", fg: "text-cream", icon: <Droplets size={32} />, title: "12× less water", body: "Per litre of milk, we use about 12× less water than almond. Rain-fed, smallholder-grown, honestly grown-up." },
            ].map((c, i) => (
              <div
                key={i}
                className={`group relative rounded-[2rem] border-2 border-jet ${c.bg} ${c.fg} p-7 shadow-pop-lg transition-transform hover:-translate-y-2 hover:rotate-[-1deg]`}
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full border-2 border-jet bg-cream text-jet">
                  {c.icon}
                </span>
                <h3 className="mt-5 font-sans text-2xl font-extrabold tracking-tight md:text-3xl">{c.title}</h3>
                <p className="mt-3 font-sans text-base leading-relaxed opacity-95 md:text-lg">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== MEET MILET MILK — cream with orange product accent ============== */}
      <section
        id="product"
        data-testid="section-product"
        className="relative overflow-hidden bg-cream py-20 text-jet md:py-28"
      >
        {/* soft glow overlays */}
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_25%,rgba(242,205,26,0.25),transparent_55%)]" />
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_85%_75%,rgba(12,0,255,0.12),transparent_55%)]" />

        <div className="relative mx-auto grid max-w-[1400px] items-center gap-12 px-6 md:grid-cols-2 md:px-10">
          <div className="relative order-2 md:order-1">
            <div className="absolute -left-4 -top-4 h-24 w-24 rotate-12 rounded-full bg-butter ring-2 ring-jet" />
            <div className="absolute -bottom-6 -right-2 h-16 w-16 rotate-45 rounded-full bg-cobalt ring-2 ring-jet" />
            <div className="relative aspect-square w-full overflow-hidden rounded-[3rem] border-[3px] border-jet bg-orange">
              <img
                src={PRODUCT_IMG}
                alt="Milet Milk"
                className="h-full w-full animate-wobble object-contain p-8"
              />
            </div>
          </div>

          <div className="order-1 md:order-2">
            <Sticker color="jet" rotate={-4}>§ what we're building</Sticker>
            <h2 className="mt-6 font-sans text-5xl font-extrabold leading-[1.05] tracking-tight text-jet md:text-6xl lg:text-[5.5rem]">
              Meet{" "}
              <span className="bg-cobalt px-2 text-cream">milet</span> milk.
            </h2>
            <p className="mt-6 max-w-lg font-sans text-lg font-semibold text-jet md:text-xl">
              The first milk you don't have to apologise for. Creamy, faintly
              nutty, genuinely drinkable. In your coffee, in your cereal, in
              your life back again!!
            </p>

            <ul className="mt-8 flex flex-wrap gap-2.5">
              {[
                { label: "Nuts free", c: "butter", r: -4 },
                { label: "Soy free", c: "cream", r: 3 },
                { label: "Lactose free", c: "butter", r: -2 },
                { label: "Dairy free", c: "cream", r: 4 },
                { label: "Gluten free", c: "cobalt", r: -3 },
              ].map((b) => (
                <Sticker key={b.label} color={b.c} rotate={b.r}>✓ {b.label}</Sticker>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#newsletter" data-testid="product-cta-newsletter" className="chunk-btn bg-cobalt text-cream">
                Get first pour <ArrowUpRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ============== VS SECTION — cream backdrop ============== */}
      <section
        data-testid="section-vs"
        className="relative overflow-hidden bg-cream py-20 text-jet md:py-28"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="flex items-end justify-between">
            <div>
              <Sticker color="butter" rotate={-4}>§ the receipts</Sticker>
              <h2 className="mt-6 font-sans text-5xl font-extrabold leading-[1.05] tracking-tight md:text-6xl lg:text-[5.5rem]">
                Why not{" "}
                <span className="bg-cobalt px-2 text-cream">the others?</span>
              </h2>
            </div>
            <Link to="/why-millets" data-testid="vs-see-all" className="chunk-btn hidden bg-cobalt text-cream md:inline-flex">
              See all <ArrowUpRight size={16} />
            </Link>
          </div>

          <div className="mt-14 grid gap-5 md:grid-cols-3">
            {[
              { t: "Almond", bad: "1 almond = 3.2L water", good: "12× less water than almond milk", bg: "bg-butter" },
              { t: "Oat", bad: "Gluten cross-contamination risk", good: "Gluten-free end to end", bg: "bg-orange", fg: "text-cream" },
              { t: "Soy", bad: "Top 8 food allergen", good: "Allergen-friendly for families", bg: "bg-cobalt", fg: "text-cream" },
            ].map((c, i) => (
              <div
                key={i}
                className={`relative overflow-hidden rounded-[2rem] border-2 border-jet ${c.bg} ${c.fg || "text-jet"} p-6 shadow-pop-lg transition-transform hover:-translate-y-2`}
              >
                <p className="font-sans text-[11px] font-extrabold uppercase tracking-[0.25em] opacity-60">Milet vs</p>
                <p className="mt-1 font-sans text-4xl font-extrabold tracking-tight md:text-5xl">{c.t}</p>
                <ul className="mt-6 space-y-2.5 text-sm">
                  <li className="flex gap-2">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cream text-[11px] font-black text-jet">×</span>
                    <span className="line-through opacity-75">{c.bad}</span>
                  </li>
                  <li className="flex gap-2">
                    <span className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-jet text-[11px] font-black text-butter">✓</span>
                    <span className="font-semibold">{c.good}</span>
                  </li>
                </ul>
              </div>
            ))}
          </div>

          <div className="mt-10 md:hidden">
            <Link to="/why-millets" data-testid="vs-see-all-mobile" className="chunk-btn bg-cobalt text-cream">
              See all 6 head-to-heads <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* ============== OUR STORY — solo founder ============== */}
      <section
        data-testid="section-story"
        className="relative overflow-hidden bg-cream py-20 md:py-28"
      >
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <div className="grid items-start gap-12 md:grid-cols-[1.15fr_1fr]">
            <div>
              <Sticker color="cobalt" rotate={-5}>§ the founder</Sticker>
              <h2 className="mt-6 font-sans text-5xl font-extrabold leading-[1.05] tracking-tight text-jet md:text-6xl lg:text-7xl">
                A consultant who once{" "}
                <span className="text-cobalt">worked the line.</span>
              </h2>

              <div className="mt-8 grid gap-5 font-sans text-lg leading-relaxed text-jet md:text-[1.18rem]">
                <p>
                  Viresh has zero business being in the food industry. No culinary degree. No nutrition cert. No "family recipe passed down for generations" story.
                </p>
                <p>
                  Just years in strategy consulting — dissecting industries, solving problems nobody asked him to solve, and telling Fortune 500s what they're doing wrong (politely, of course).
                </p>
                <p>
                  But rewind the tape far enough and he was a line cook at a university pub. Not glamorous. Not Michelin-starred. Enough to know what real food feels like — the weight of it, the rhythm of a kitchen, and the difference between something made with care and something made in a factory.
                </p>

                <p className="rounded-2xl border-2 border-jet bg-cobalt p-5 font-sans text-lg font-bold leading-snug text-cream md:text-xl">
                  And here's the thing about strategy consultants — they see patterns everywhere. The pattern was clear: the dairy aisle is broken.
                </p>

                <p>
                  Too much junk dressed up as "healthy." Too many ingredient lists that read like a PhD thesis. Not enough real, clean, simple alternatives.
                </p>
                <p>
                  So now he's doing what any self-respecting consultant would do — <strong>not writing a 90-slide deck about it, but actually building the thing.</strong> No legacy thinking. No "but that's how it's always been done." Just fresh eyes, sharp strategy, and a stubborn belief that better milk shouldn't be complicated.
                </p>
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Sticker color="butter">Viresh · Toronto</Sticker>
                <Sticker color="cobalt">ex-strategy · ex-line cook</Sticker>
              </div>
            </div>

            {/* Founder portrait — single, tall, editorial */}
            <div className="relative">
              <div
                data-testid="founder-card-viresh"
                className="group relative mx-auto aspect-[3/4] w-full max-w-md overflow-hidden rounded-[2.5rem] border-[3px] border-jet bg-cobalt shadow-pop-lg transition-transform hover:-translate-y-1 hover:-rotate-1"
              >
                <img
                  src={VIRESH_IMG}
                  alt="Viresh Maheshwari, founder of Milet"
                  className="absolute inset-0 h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0"
                />
                <div className="absolute inset-x-4 bottom-4 rounded-2xl border-2 border-jet bg-cream p-4">
                  <p className="font-sans text-[10px] font-extrabold uppercase tracking-[0.22em] text-cobalt">
                    Founder · Toronto
                  </p>
                  <p className="mt-0.5 font-sans text-2xl font-extrabold leading-none tracking-tight text-jet">
                    Viresh Maheshwari
                  </p>
                </div>

                {/* floating tag accents */}
                <span className="absolute left-3 top-3 rotate-[-6deg] rounded-full border-2 border-jet bg-butter px-3 py-1 font-sans text-[10px] font-black uppercase tracking-[0.22em] text-jet">
                  § the line cook
                </span>
                <span className="absolute right-3 top-3 rotate-[5deg] rounded-full border-2 border-jet bg-orange px-3 py-1 font-sans text-[10px] font-black uppercase tracking-[0.22em] text-cream">
                  the consultant
                </span>
              </div>

              {/* sticky pull-quote sticker beneath */}
              <div className="mx-auto mt-6 max-w-md rounded-[1.5rem] border-2 border-jet bg-orange p-4 text-cream shadow-pop">
                <p className="font-sans text-base font-bold leading-snug md:text-lg">
                  "Better milk shouldn't be complicated. So I'm building the one I actually want to drink."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== NEWSLETTER ============== */}
      <NewsletterCTA source="home-hero" id="newsletter" />

      <Marquee
        items={["milet", "the future of dairy", "milet", "est. 2025", "milet", "canada"]}
        bg="bg-cobalt"
        fg="text-butter"
      />
    </main>
  );
}
